import type { DiscountResponse, PlanDiscount } from '@/services/subscriptions';
import { getPlanDiscount } from './pricing';

const DISCOUNT_PLAN_KEYS = ['MONTHLY', 'ANNUAL', 'FIVE_YEAR'] as const;

export const getDiscountExpiresAt = (
  discount: DiscountResponse | null
): Date | null => {
  const expiresAt = DISCOUNT_PLAN_KEYS.map(
    (planKey) => getPlanDiscount(discount, planKey)?.expiresAt
  ).filter((value): value is string => value !== null && value !== undefined);

  if (expiresAt.length === 0) return null;

  const dates = expiresAt.map((value) => new Date(value));

  if (dates.some((date) => !Number.isFinite(date.getTime()))) return null;

  return new Date(Math.min(...dates.map((date) => date.getTime())));
};

// The countdown is for the same-night deadline (School Launch's PRIMARY phase
// ends at school-local midnight). After that deadline the STEP_DOWN rule takes
// over with an expiry ~30 days out, which would render as e.g. `719:59:59`, so
// only expiries within the next day get a timer.
export const COUNTDOWN_MAX_REMAINING_MS = 24 * 60 * 60 * 1000;

export const shouldShowCountdown = (
  expiresAt: Date | null,
  nowMs: number = Date.now()
): expiresAt is Date => {
  if (!expiresAt) return false;

  const remainingMs = expiresAt.getTime() - nowMs;
  return remainingMs > 0 && remainingMs <= COUNTDOWN_MAX_REMAINING_MS;
};

export const formatCountdown = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

// School Launch coupons are catalogued by the backend as
// `School Launch Offer — N% PLAN` (and the rule itself is `School Launch
// Offer`), so the per-plan coupon name identifies a launch-cohort discount
// without a dedicated payload field. Every other offer family uses its own
// naming (partner names, "Summer Sale", "Referral Bonus").
export const SCHOOL_LAUNCH_COUPON_NAME_PREFIX = 'School Launch Offer';

const getSchoolLaunchPlanDiscounts = (
  discount: DiscountResponse | null | undefined
): PlanDiscount[] =>
  DISCOUNT_PLAN_KEYS.map((planKey) =>
    getPlanDiscount(discount, planKey)
  ).filter(
    (plan): plan is PlanDiscount =>
      plan?.name?.startsWith(SCHOOL_LAUNCH_COUPON_NAME_PREFIX) === true
  );

export const isSchoolLaunchDiscount = (
  discount: DiscountResponse | null | undefined
): boolean => getSchoolLaunchPlanDiscounts(discount).length > 0;

// The deadline the in-app countdown (ORA-892) ticks toward: the School Launch
// phase currently in force. The backend resolves PRIMARY until the school's
// offer deadline and STEP_DOWN for the 30 days after it, so a School Launch
// plan's expiry is always the current phase's end. Plans resolve
// independently, so only the School Launch plans' expiries count — a partner
// coupon on another plan must not set (or suppress) this deadline. Unlike the
// thank-you page's same-night timer, a phase can end days out, so there is no
// 24h cap — only a future expiry qualifies.
export const getSchoolLaunchDeadline = (
  discount: DiscountResponse | null | undefined,
  nowMs: number = Date.now()
): Date | null => {
  const expiryMs = getSchoolLaunchPlanDiscounts(discount)
    .map((plan) => (plan.expiresAt ? new Date(plan.expiresAt).getTime() : NaN))
    .filter((value) => Number.isFinite(value));

  if (expiryMs.length === 0) return null;

  const earliest = Math.min(...expiryMs);
  return earliest > nowMs ? new Date(earliest) : null;
};

const SECONDS_PER_DAY = 24 * 60 * 60;

// `HH:MM:SS` under a day, `Nd HH:MM:SS` beyond it, so a step-down deadline
// weeks away reads as `12d 04:13:09` rather than `292:13:09`.
export const formatDeadlineCountdown = (remainingMs: number): string => {
  const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
  if (totalSeconds < SECONDS_PER_DAY) return formatCountdown(remainingMs);

  const days = Math.floor(totalSeconds / SECONDS_PER_DAY);
  const remainderSeconds = totalSeconds - days * SECONDS_PER_DAY;
  return `${days}d ${formatCountdown(remainderSeconds * 1000)}`;
};
